import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import {
    getRepertoireSuccess,
    getRepertoireFail,
    getAllRepertoiresSuccess,
    getAllRepertoiresFail,
    getRepertoireTreeSuccess,
    getRepertoireTreeFail,
    getAuthorizedRepertoireTreeSuccess,
    getAuthorizedRepertoireTreeFail,
    getAccessibleRepertoireTreeSuccess,
    getAccessibleRepertoireTreeFail,
    getRepertoireContextSuccess,
    getRepertoireContextFail,
    getRepertoireBreadcrumbSuccess,
    getRepertoireBreadcrumbFail,
    getRepertoiresByStatusSuccess,
    getRepertoiresByStatusFail,
    createRepertoireSuccess,
    createRepertoireFail,
    updateRepertoireSuccess,
    updateRepertoireFail,
    changeRepertoireStatusSuccess,
    changeRepertoireStatusFail,
    deleteRepertoireSuccess,
    deleteRepertoireFail,
    setRepertoireLoading,
    setSelectedRepertoire,
    setRepertoireExpandedKeys
} from "../actions/repertoireActions";

import {
    API_BASE_URL,
    extractErrorMessage,
    getRequest,
    postRequest,
    putRequest,
    patchRequest,
    deleteRequest
} from "../../../api";

const getUidHeaderAndToken = () => ({
    uid: sessionStorage.getItem("uid") || localStorage.getItem("uid"),
    token: sessionStorage.getItem("token") || localStorage.getItem("token"),
});

function buildExpandedKeysFromBreadcrumb(breadcrumb = []) {
    const expanded = {};
    breadcrumb.forEach((item) => {
        expanded[item.id] = true;
    });
    return expanded;
}

export function* getRepertoireSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/${action.payload.id}`;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getRepertoireSuccess(resp.data));
    } catch (error) {
        yield put(getRepertoireFail(extractErrorMessage(error, "Erreur de chargement du repertoire")));
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* getAllRepertoiresSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires`;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getAllRepertoiresSuccess(resp.data));
    } catch (error) {
        yield put(getAllRepertoiresFail(extractErrorMessage(error, "Erreur de chargement")));
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* getRepertoireTreeSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/tree`;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getRepertoireTreeSuccess(resp.data));
    } catch (error) {
        yield put(getRepertoireTreeFail(extractErrorMessage(error, "Erreur de chargement de l'arbre")));
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* getAuthorizedRepertoireTreeSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/tree/authorized`;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getAuthorizedRepertoireTreeSuccess(resp.data));
    } catch (error) {
        yield put(getAuthorizedRepertoireTreeFail(extractErrorMessage(error, "Erreur de chargement des acces")));
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* getAccessibleRepertoireTreeSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/tree/accessible`;

    try {
        const resp = yield call(getRequest, { url, token });
        const tree = resp.data || [];
        yield put(getAccessibleRepertoireTreeSuccess(tree));

        const selectedId = sessionStorage.getItem("kmsSelectedRepertoireId");
        const firstRoot = tree.length > 0 ? tree[0] : null;
        const targetId = selectedId || firstRoot?.id;

        if (targetId) {
            yield put(setSelectedRepertoire(Number(targetId)));
        }
    } catch (error) {
        yield put(getAccessibleRepertoireTreeFail(extractErrorMessage(error, "Erreur de chargement de l'arbre KMS")));
    }
}

export function* getRepertoireContextSaga(action) {
    const { token } = getUidHeaderAndToken();
    const repertoireId = action.payload.id;
    const url = `${API_BASE_URL}/api/repertoires/${repertoireId}/context`;

    try {
        const resp = yield call(getRequest, { url, token });
        const context = resp.data;
        yield put(getRepertoireContextSuccess(context));
        yield put(setSelectedRepertoire(repertoireId));

        sessionStorage.setItem("kmsSelectedRepertoireId", String(repertoireId));
        yield put(setRepertoireExpandedKeys(buildExpandedKeysFromBreadcrumb(context?.breadcrumb)));
    } catch (error) {
        yield put(getRepertoireContextFail(extractErrorMessage(error, "Erreur de chargement du contexte")));
    }
}

export function* getRepertoireBreadcrumbSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/${action.payload.id}/breadcrumb`;

    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getRepertoireBreadcrumbSuccess(resp.data));
    } catch (error) {
        yield put(getRepertoireBreadcrumbFail(extractErrorMessage(error, "Erreur de chargement du breadcrumb")));
    }
}

export function* getRepertoiresByStatusSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/status/${action.payload.status}`;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getRepertoiresByStatusSuccess(resp.data));
    } catch (error) {
        yield put(getRepertoiresByStatusFail(extractErrorMessage(error, "Erreur de filtrage")));
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* createRepertoireSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires`;
    const data = action.payload;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(postRequest, { url, data, token });
        yield put(createRepertoireSuccess(resp.data));
        yield put({ type: 'GET_REPERTOIRE_TREE_REQUEST' });

        yield call([Swal, Swal.fire], {
            icon: 'success',
            text: 'Repertoire cree avec succes',
            timer: 2000,
            showConfirmButton: false
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la creation");
        yield put(createRepertoireFail(message));

        yield call([Swal, Swal.fire], {
            icon: 'error',
            text: message,
            confirmButtonText: 'OK'
        });
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* updateRepertoireSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/${action.payload.id}`;
    const data = action.payload.data;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(putRequest, { url, data, token });
        yield put(updateRepertoireSuccess(resp.data));
        yield put({ type: 'GET_REPERTOIRE_TREE_REQUEST' });
        yield put({ type: 'GET_ACCESSIBLE_REPERTOIRE_TREE_REQUEST' });

        yield call([Swal, Swal.fire], {
            icon: 'success',
            text: 'Repertoire mis a jour avec succes',
            timer: 2000,
            showConfirmButton: false
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la mise a jour");
        yield put(updateRepertoireFail(message));

        yield call([Swal, Swal.fire], {
            icon: 'error',
            text: message,
            confirmButtonText: 'OK'
        });
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* changeRepertoireStatusSaga(action) {
    const { id, status } = action.payload;
    const { uid, token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/${id}/status?status=${status}`;

    yield put(setRepertoireLoading(true));
    try {
        const resp = yield call(patchRequest, { url, data: {}, uid, token });
        yield put(changeRepertoireStatusSuccess(resp.data));
        yield put({ type: 'GET_REPERTOIRE_TREE_REQUEST' });
        yield put({ type: 'GET_ACCESSIBLE_REPERTOIRE_TREE_REQUEST' });

        yield call([Swal, Swal.fire], {
            icon: 'success',
            text: 'Statut mis a jour avec succes',
            timer: 1800,
            showConfirmButton: false
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du changement de statut");
        yield put(changeRepertoireStatusFail(message));

        yield call([Swal, Swal.fire], {
            icon: 'error',
            text: message,
            confirmButtonText: 'OK'
        });
    } finally {
        yield put(setRepertoireLoading(false));
    }
}

export function* deleteRepertoireSaga(action) {
    const { id } = action.payload;
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/repertoires/${id}`;

    yield put(setRepertoireLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteRepertoireSuccess(id));
        yield put({ type: 'GET_REPERTOIRE_TREE_REQUEST' });
        yield put({ type: 'GET_ACCESSIBLE_REPERTOIRE_TREE_REQUEST' });

        yield call([Swal, Swal.fire], {
            icon: 'success',
            text: 'Repertoire archivé avec succes',
            timer: 1800,
            showConfirmButton: false
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la suppression");
        yield put(deleteRepertoireFail(message));

        yield call([Swal, Swal.fire], {
            icon: 'error',
            text: message,
            confirmButtonText: 'OK'
        });
    } finally {
        yield put(setRepertoireLoading(false));
    }
}
